import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import {
    Template,
    MessageComposition,
    Group,
    Routing,
    RoutingTarget,
    RoutingFieldMapping,
    SendMode,
    TruncateStrategy,
    ChannelType,
    ChannelConfig,
    TemplateVariable,
    ProviderBinding,
    PageResult,
    ProviderTemplate,
    ProviderTemplateButton,
    SesTemplate,
    SesConfigurationSet,
    TemplateRole,
    EmailEnvelope,
    EmailBlock,
    EmailEditorMode,
    emptyChannelConfig,
} from '../types/communication';
import { envelopeFromJson, envelopeToJson } from '../utils/emailEnvelope';

const API = appConfig.apis.communicationApiEnv;
const baseUrl = API.address;

const API_NAME = 'Communication API';
const API_VERSION = 'v1';

export interface ErrorField {
    fieldName: string;
    message: string;
}

export class CommunicationApiError extends Error {
    constructor(public status: number, message: string, public errors: ErrorField[] = []) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204) return null;
        const text = await res.text();
        return text ? JSON.parse(text) : null;
    }
    const data = await res.json().catch(() => null);
    const errors: ErrorField[] = Array.isArray(data?.errors) ? data.errors : [];
    throw new CommunicationApiError(res.status, data?.message || `API error: ${res.status}`, errors);
};

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(
        await fetch(`${baseUrl}${path}`, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(data),
        }),
    );

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(
        await fetch(`${baseUrl}${path}`, {
            method: 'PUT',
            headers: await getHeaders(),
            body: JSON.stringify(data),
        }),
    );

const patch = async (path: string, data: Record<string, unknown>) =>
    handleResponse(
        await fetch(`${baseUrl}${path}`, {
            method: 'PATCH',
            headers: await getHeaders(),
            body: JSON.stringify(data),
        }),
    );

const del = async (path: string, data?: Record<string, unknown>) =>
    handleResponse(
        await fetch(`${baseUrl}${path}`, {
            method: 'DELETE',
            headers: await getHeaders(),
            body: data ? JSON.stringify(data) : undefined,
        }),
    );

const qs = (params: Record<string, unknown>): string => {
    const s = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== '') s.set(k, String(v));
    });
    const str = s.toString();
    return str ? `?${str}` : '';
};

/**
 * Desembrulha a resposta de page-list do backend do communication para o PageResult<T>
 * que os hooks e telas do protótipo já consomem (page 0-based + pageSize). Mantém a superfície
 * pública estável.
 *
 * O backend responde com a lista em `list` e `total`/`page`/`size`/`apiVersion`/`apiName` no root
 * (page 1-based). O fallback para o envelope `{data, meta}` cobre endpoints que sigam o padrão
 * REST genérico do monorepo.
 */
const toPageResult = <T>(payload: any): PageResult<T> => {
    const meta = payload?.meta || {};
    const list: T[] = Array.isArray(payload?.list)
        ? payload.list
        : Array.isArray(payload?.data)
            ? payload.data
            : [];
    const rawPage = payload?.page ?? meta.page;
    const rawSize = payload?.size ?? meta.size;
    const rawTotal = payload?.total ?? meta.totalElements;
    const page = rawPage != null ? Number(rawPage) - 1 : 0;
    const pageSize = rawSize != null ? Number(rawSize) : list.length;
    const totalElements = rawTotal != null ? Number(rawTotal) : list.length;
    const totalPages =
        meta.totalPages != null
            ? Number(meta.totalPages)
            : pageSize > 0
                ? Math.ceil(totalElements / pageSize)
                : 1;
    return {
        list,
        page: page < 0 ? 0 : page,
        pageSize,
        totalElements,
        totalPages,
        numberOfElements: list.length,
        size: pageSize,
        apiName: payload?.apiName || meta.apiName || API_NAME,
        apiVersion: payload?.apiVersion || meta.apiVersion || API_VERSION,
    };
};

const unwrap = <T>(payload: any): T => (payload && 'data' in payload ? payload.data : payload);

/**
 * Mapeia um template embutido no page-view da composição (uuid, role, position, content,
 * variables) para a forma Template que o studio renderiza. role vem como MESSAGE/BODY/ENVELOPE;
 * só ENVELOPE vira role de envelope, o resto é corpo. O canal é o da composição.
 */
const editorFieldsOf = (raw: any): Pick<Template, 'blocks' | 'editorMode' | 'providerBindings' | 'contentUnavailable'> => {
    const providerParameters: any[] = Array.isArray(raw?.providerParameters) ? raw.providerParameters : [];
    const editorSource = raw?.editorSource ?? null;
    return {
        blocks: Array.isArray(editorSource?.blocks) ? editorSource.blocks : undefined,
        editorMode: editorSource?.editorMode === 'BLOCKS' || editorSource?.editorMode === 'CODE'
            ? editorSource.editorMode
            : undefined,
        providerBindings: providerParameters.map((p) => ({
            placeholder: String(p?.position ?? ''),
            variable: p?.variable ?? '',
        })),
        contentUnavailable: Boolean(raw?.contentUnavailable),
    };
};

const toEmbeddedTemplate = (raw: any, channel: ChannelType): Template => {
    const isEnvelope = (raw?.role ?? '').toUpperCase() === 'ENVELOPE';
    return {
        id: raw?.uuid ?? raw?.id ?? '',
        name: raw?.name ?? '',
        description: raw?.description ?? '',
        channel,
        type: raw?.format ?? null,
        providerTemplateName: raw?.providerRef ?? null,
        content: raw?.content ?? '',
        role: isEnvelope ? 'ENVELOPE' : 'BODY',
        envelope: isEnvelope ? envelopeFromJson(raw?.content) : undefined,
        ...editorFieldsOf(raw),
        variables: Array.isArray(raw?.variables) ? raw.variables : [],
        messageCompositionId: null,
        status: raw?.status ?? 'ACTIVE',
        createdAt: raw?.createdAt ?? '',
        updatedAt: raw?.updatedAt ?? '',
    };
};

/**
 * Mapeia o page-view/page-list de MessageComposition (contrato do backend: uuid, groupUuid,
 * channelConfig em channelSettings, templates[{uuid,role,position,content,variables}]) para o tipo
 * MessageComposition do frontend. Preserva os templates completos (com conteúdo) do page-view —
 * o page-list de templates não traz content, então a tela precisa do que vem embutido aqui.
 */
const toMessageComposition = (raw: any): MessageComposition => {
    const channel: ChannelType = raw.channel;
    const rawTemplates: any[] = Array.isArray(raw.templates) ? raw.templates : [];
    const orderedTemplates = [...rawTemplates].sort(
        (a, b) => (a?.position ?? 0) - (b?.position ?? 0),
    );
    const templates: Template[] = orderedTemplates.map((t) => toEmbeddedTemplate(t, channel));
    const channelConfig: ChannelConfig =
        raw.channelConfig || (raw.channelSettings && raw.channelSettings.channelConfig) || emptyChannelConfig(channel);
    return {
        id: raw.uuid ?? raw.id,
        serialId: typeof raw.id === 'number' ? raw.id : undefined,
        sendId: typeof raw.sendId === 'string' && raw.sendId ? raw.sendId : null,
        name: raw.name ?? '',
        description: raw.description ?? '',
        channel,
        templateIds: templates.map((t) => t.id).filter(Boolean),
        templates,
        groupId: raw.groupUuid ?? raw.groupId ?? null,
        groupName: raw.groupName ?? null,
        templateCount: typeof raw.templateCount === 'number' ? raw.templateCount : undefined,
        variableCount: typeof raw.variableCount === 'number' ? raw.variableCount : undefined,
        channelConfig,
        raw: raw.raw ?? { id: raw.uuid ?? '', type: CHANNEL_TO_TYPE[channel], templates: [] },
        status: raw.status ?? 'ACTIVE',
        statusReason: raw.statusReason ?? null,
        version: raw.version,
        createdAt: raw.createdAt ?? '',
        updatedAt: raw.updatedAt ?? '',
    };
};

const CHANNEL_TO_TYPE: Record<ChannelType, 'MAIL' | 'SLACK' | 'WPP'> = {
    EMAIL: 'MAIL',
    INTERNAL_MESSAGE: 'SLACK',
    EXTERNAL_MESSAGE: 'WPP',
};

/** Mapeia o grupo do backend (uuid, members[]) para o tipo Group do frontend (id, users[]). */
const toGroup = (raw: any): Group => ({
    id: raw.uuid ?? raw.id,
    name: raw.name ?? '',
    description: raw.description ?? '',
    defaultForNewContactPoints: Boolean(raw.defaultForNewContactPoints),
    users: Array.isArray(raw.members)
        ? raw.members.map((m: any) => ({
              login: m.userLogin,
              joinedAt: m.joinedAt ?? '',
              active: true,
              removedAt: null,
              removalReason: null,
          }))
        : Array.isArray(raw.users)
            ? raw.users
            : [],
    status: raw.status ?? 'ACTIVE',
    statusReason: raw.statusReason ?? null,
    createdAt: raw.createdAt ?? '',
    updatedAt: raw.updatedAt ?? '',
});

/**
 * Mapeia a regra crua de roteamento do backend para a forma Routing que a tela consome.
 * O backend expõe eventNames[], messageCompositionUuid e channel achatados (page-list) ou
 * com conditions[]/variableMapping (page-view); a tela espera event, targets[] e conditions[].
 * environments pode vir null do backend e precisa virar lista vazia. O channel vem no vocabulário
 * legado (MAIL/WPP/SLACK) e é traduzido para o ChannelType da tela.
 */
const LEGACY_CHANNEL_MAP: Record<string, ChannelType> = {
    MAIL: 'EMAIL',
    EMAIL: 'EMAIL',
    WPP: 'EXTERNAL_MESSAGE',
    EXTERNAL_MESSAGE: 'EXTERNAL_MESSAGE',
    SLACK: 'INTERNAL_MESSAGE',
    INTERNAL_MESSAGE: 'INTERNAL_MESSAGE',
};

const toChannelType = (raw: string | null | undefined): ChannelType =>
    LEGACY_CHANNEL_MAP[(raw ?? '').toUpperCase()] ?? 'INTERNAL_MESSAGE';

const toRouting = (raw: any): Routing => {
    const eventNames: string[] = Array.isArray(raw?.eventNames) ? raw.eventNames : [];
    const rawConditions: any[] = Array.isArray(raw?.conditions) ? raw.conditions : [];
    const fieldMappings: RoutingFieldMapping[] = Object.entries(raw?.variableMapping ?? {}).map(
        ([variableName, eventField]) => ({ variableName, eventField: String(eventField) }),
    );
    const targets: RoutingTarget[] =
        raw?.messageCompositionUuid != null
            ? [
                  {
                      messageCompositionId: raw.messageCompositionUuid,
                      channel: toChannelType(raw.channel),
                      groupName: null,
                      sendMode: 'IMMEDIATE' as SendMode,
                      scheduledAt: null,
                      groupByPayer: false,
                      fieldMappings,
                      limits: { maxItems: 0, truncateStrategy: 'full' as TruncateStrategy },
                  },
              ]
            : [];
    return {
        id: raw?.ruleId ?? '',
        ruleId: raw?.ruleId ?? '',
        event: eventNames.join(', '),
        description: raw?.description ?? '',
        conditions: rawConditions.map((c: any) => ({
            jsonPath: c?.jsonPath ?? c?.field ?? '',
            equals: c?.equals ?? (Array.isArray(c?.in) ? c.in.join(', ') : ''),
        })),
        targets,
        enabled: Boolean(raw?.enabled),
        environments: Array.isArray(raw?.environments) ? raw.environments : [],
    };
};

/**
 * Mapeia o item cru de template (page-list) para a forma Template que as telas consomem.
 * O backend expõe uuid, channel, role, messageCompositionUuid e updatedAt (que pode vir null
 * quando o template nunca sofreu update); a tela usa id, messageCompositionId e ordena por
 * updatedAt/name, por isso os ausentes viram string vazia.
 */
const toTemplate = (raw: any): Template => ({
    id: raw?.uuid ?? raw?.id ?? '',
    name: raw?.name ?? '',
    description: raw?.description ?? '',
    channel: raw?.channel ?? 'INTERNAL_MESSAGE',
    type: raw?.format ?? null,
    providerTemplateName: raw?.providerRef ?? null,
    content: raw?.content ?? '',
    role: raw?.role ?? (raw?.format === 'JSON' ? 'ENVELOPE' : undefined),
    ...editorFieldsOf(raw),
    variables: Array.isArray(raw?.variables) ? raw.variables : [],
    messageCompositionId: raw?.messageCompositionUuid ?? null,
    status: raw?.status ?? 'ACTIVE',
    createdAt: raw?.createdAt ?? '',
    updatedAt: raw?.updatedAt ?? '',
});

/**
 * Mapeia o item cru do catálogo da Blip (components[] no formato Meta, snake_case) para o
 * ProviderTemplate achatado que os componentes de WhatsApp consomem. Deriva o selo de
 * recategorização de previous_category ≠ category.
 */
const toProviderTemplate = (raw: any): ProviderTemplate => {
    const components: any[] = Array.isArray(raw?.components) ? raw.components : [];
    const byType = (type: string) => components.find((c) => (c?.type ?? '').toUpperCase() === type);
    const headerComp = byType('HEADER');
    const bodyComp = byType('BODY');
    const footerComp = byType('FOOTER');
    const buttonsComp = byType('BUTTONS');

    const headerFormat = (headerComp?.format ?? '').toUpperCase();
    const header: ProviderTemplate['header'] =
        headerFormat === 'DOCUMENT'
            ? { type: 'DOCUMENT' }
            : headerFormat === 'TEXT' || headerComp?.text
                ? { type: 'TEXT', text: headerComp?.text ?? '' }
                : { type: 'NONE' };

    const buttons: ProviderTemplateButton[] = Array.isArray(buttonsComp?.buttons)
        ? buttonsComp.buttons.map((b: any) => ({
              type: (b?.type ?? '').toUpperCase() === 'URL' ? 'URL' : 'QUICK_REPLY',
              text: b?.text ?? '',
          }))
        : [];

    const previousCategory = raw?.previous_category ?? undefined;
    const category = raw?.category ?? 'UTILITY';

    return {
        name: raw?.name ?? '',
        language: raw?.language ?? '',
        category,
        status: (raw?.status ?? 'PENDING').toUpperCase(),
        header,
        body: bodyComp?.text ?? '',
        footer: footerComp?.text,
        buttons,
        previousCategory,
        recategorized: Boolean(previousCategory && previousCategory !== category),
        updatedAt: raw?.last_updated_time ?? '',
    };
};

interface TemplateFilters {
    search?: string;
    channel?: ChannelType | '';
    status?: 'ACTIVE' | 'INACTIVE' | '';
    page?: number;
    pageSize?: number;
}

interface MessageCompositionFilters {
    search?: string;
    channel?: ChannelType | '';
    status?: 'ACTIVE' | 'INACTIVE' | '';
    groupId?: string;
    page?: number;
    pageSize?: number;
    sortField?: 'name' | 'updatedAt' | 'status';
    sortOrder?: 'ASC' | 'DESC';
}

export interface DirectoryUser {
    username: string;
    name?: string;
    email?: string;
}

export interface PreviewRender {
    role: string;
    templateUuid: string | null;
    rendered: string;
}

export interface ReviseStudioTemplate {
    id: string;
    channel: ChannelType;
    role?: string;
    name: string;
    content: string;
    subject?: string;
    providerRef?: string;
    providerBindings?: ProviderBinding[];
    editorSource?: { editorMode?: string; blocks?: unknown[] } | null;
    variables: TemplateVariable[];
}

export interface ReviseCompositionInput {
    name: string;
    description: string;
    groupId: string | null;
    channelConfig: ChannelConfig;
    deliveryPolicy?: { identifier?: string[]; groupedItems?: string[]; attachmentZipPrefix?: string } | null;
    version?: number;
    templates: ReviseStudioTemplate[];
}

/** Ids de rascunho começam com "draft-"; o backend recebe uuid=null para criar o modelo. */
const isPersistedId = (id: string): boolean => !id.startsWith('draft-');

export interface TemplateDraftInput {
    name: string;
    description: string;
    channel: ChannelType;
    role?: TemplateRole;
    providerTemplateName: string | null;
    content: string;
    envelope?: EmailEnvelope;
    blocks?: EmailBlock[];
    editorMode?: EmailEditorMode;
    providerBindings?: ProviderBinding[];
    variables: TemplateVariable[];
}

export interface SavedRef {
    id: string;
    version?: number;
}

const ENVELOPE_SCHEMA = 'communication.email-envelope.v1';

const providerRefFromName = (name: string): string =>
    name.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'TEMPLATE';

const isEnvelopeDraft = (t: Pick<TemplateDraftInput, 'channel' | 'role'>): boolean =>
    t.channel === 'EMAIL' && t.role === 'ENVELOPE';

const compositionRole = (t: { channel: ChannelType; role?: string }): 'BODY' | 'ENVELOPE' | 'MESSAGE' => {
    if (t.channel !== 'EMAIL') return 'MESSAGE';
    return t.role === 'ENVELOPE' ? 'ENVELOPE' : 'BODY';
};

const storageOf = (t: TemplateDraftInput): Record<string, unknown> => {
    if (isEnvelopeDraft(t)) return { storage: 'APPLICATION', format: 'JSON', contentSchema: ENVELOPE_SCHEMA };
    if (t.channel === 'EMAIL') {
        return {
            storage: 'PROVIDER',
            format: 'HTML',
            provider: 'SES',
            providerRef: t.providerTemplateName?.trim() || providerRefFromName(t.name),
        };
    }
    if (t.channel === 'INTERNAL_MESSAGE') return { storage: 'APPLICATION', format: 'MARKDOWN' };
    return { storage: 'PROVIDER', format: null, provider: 'BLIP', providerRef: t.providerTemplateName ?? null };
};

const draftContent = (t: TemplateDraftInput): string =>
    isEnvelopeDraft(t) && t.envelope ? envelopeToJson(t.envelope) : t.content;

const editorSourceOf = (t: TemplateDraftInput) =>
    t.editorMode && t.blocks ? { editorMode: t.editorMode, blocks: t.blocks } : null;

const providerParametersOf = (t: TemplateDraftInput) =>
    (t.providerBindings ?? []).map((b) => ({ position: b.placeholder, variable: b.variable }));

const subjectOf = (t: TemplateDraftInput): Record<string, string> =>
    t.envelope?.subject ? { subject: t.envelope.subject } : {};

const approvedModelOf = (t: TemplateDraftInput): Record<string, string> =>
    t.channel === 'EXTERNAL_MESSAGE' && t.providerTemplateName ? { providerRef: t.providerTemplateName } : {};

const revisionBody = (t: TemplateDraftInput): Record<string, unknown> => ({
    content: draftContent(t),
    description: t.description,
    variables: t.variables,
    editorSource: editorSourceOf(t),
    providerParameters: providerParametersOf(t),
    ...subjectOf(t),
});

const draftBody = (t: TemplateDraftInput): Record<string, unknown> => ({
    name: t.name,
    ...storageOf(t),
    ...revisionBody(t),
});

const savedRef = (payload: any, fallbackId = ''): SavedRef => {
    const data = unwrap<any>(payload) ?? {};
    return { id: data.uuid ?? fallbackId, ...(data.version != null ? { version: Number(data.version) } : {}) };
};

export interface SystemConstant {
    name: string;
    label: string;
    scope: string;
    description: string;
}

const listPageParams = (f: TemplateFilters | MessageCompositionFilters): Record<string, unknown> => {
    const params: Record<string, unknown> = {
        search: f.search,
        channel: f.channel,
        status: f.status,
    };
    if (f.page != null) params.page = f.page + 1;
    if (f.pageSize != null) params.size = f.pageSize;
    return params;
};

export const communicationApi = {
    // ============ Template ============
    listTemplates: async (filters: TemplateFilters = {}): Promise<PageResult<Template>> => {
        const payload = await get(`/templates/page-list${qs(listPageParams(filters))}`);
        const page = toPageResult<any>(payload);
        return { ...page, list: page.list.map(toTemplate) };
    },

    getTemplate: async (uuid: string): Promise<Template> =>
        toTemplate(unwrap<any>(await get(`/templates/${uuid}/page-view`))),

    createTemplate: async (input: TemplateDraftInput): Promise<SavedRef> =>
        savedRef(await post(`/templates`, draftBody(input))),

    reviseFreeTemplate: async (uuid: string, input: TemplateDraftInput): Promise<SavedRef> =>
        savedRef(await put(`/templates/${uuid}`, { ...revisionBody(input), ...approvedModelOf(input) }), uuid),

    duplicateTemplate: async (uuid: string): Promise<Template> =>
        toTemplate(unwrap<any>(await post(`/templates/${uuid}/duplicates`, {}))),

    linkTemplate: async (
        template: Pick<Template, 'id' | 'channel' | 'role'>,
        compositionUuid: string,
        position: number,
    ): Promise<void> => {
        await post(`/message-compositions/${compositionUuid}/templates`, {
            templateUuid: template.id,
            role: compositionRole(template),
            position,
        });
    },

    unlinkTemplate: async (templateUuid: string, compositionUuid?: string): Promise<void> => {
        if (!compositionUuid) {
            const template = await communicationApi.getTemplate(templateUuid);
            compositionUuid = template.messageCompositionId || '';
        }
        if (compositionUuid) {
            await del(`/message-compositions/${compositionUuid}/templates/${templateUuid}`);
        }
    },

    toggleTemplateStatus: async (uuid: string, reason: string): Promise<Template> => {
        const current = await communicationApi.getTemplate(uuid);
        const next = current.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        return toTemplate(unwrap<any>(await patch(`/templates/${uuid}/status`, { status: next, reason })));
    },

    getProviderCatalog: async (provider = 'BLIP'): Promise<ProviderTemplate[]> => {
        const payload = await get(`/templates/provider-catalog${qs({ provider })}`);
        const items = unwrap<any[]>(payload) || [];
        return (Array.isArray(items) ? items : []).map(toProviderTemplate);
    },

    getSesTemplates: async (): Promise<SesTemplate[]> => {
        const payload = await get(`/templates/provider-catalog${qs({ provider: 'SES', resource: 'templates' })}`);
        const items = unwrap<any[]>(payload) || [];
        return (Array.isArray(items) ? items : [])
            .filter((i) => i && typeof i.name === 'string')
            .map((i) => ({ name: i.name, createdTimestamp: i.createdTimestamp ?? '' }));
    },

    getSesConfigurationSets: async (): Promise<SesConfigurationSet[]> => {
        const payload = await get(
            `/templates/provider-catalog${qs({ provider: 'SES', resource: 'configuration-sets' })}`,
        );
        const items = unwrap<any[]>(payload) || [];
        return (Array.isArray(items) ? items : [])
            .filter((i) => i && typeof i.name === 'string')
            .map((i) => ({ name: i.name }));
    },

    // ============ MessageComposition ============
    listMessageCompositions: async (
        filters: MessageCompositionFilters = {},
    ): Promise<PageResult<MessageComposition>> => {
        const params = listPageParams(filters);
        if ((filters as MessageCompositionFilters).groupId) {
            params.groupUuid = (filters as MessageCompositionFilters).groupId;
        }
        if ((filters as MessageCompositionFilters).sortField) {
            const dir = (filters as MessageCompositionFilters).sortOrder === 'DESC' ? '-' : '';
            params.sort = `${dir}${(filters as MessageCompositionFilters).sortField}`;
        }
        const payload = await get(`/message-compositions/page-list${qs(params)}`);
        const page = toPageResult<any>(payload);
        return { ...page, list: page.list.map(toMessageComposition) };
    },

    getMessageComposition: async (uuid: string): Promise<MessageComposition> =>
        toMessageComposition(unwrap<any>(await get(`/message-compositions/${uuid}/page-view`))),

    createMessageComposition: async (input: {
        name: string;
        description: string;
        channel: ChannelType;
        groupId: string | null;
        channelConfig: ChannelConfig;
    }): Promise<SavedRef> =>
        savedRef(
            await post(`/message-compositions`, {
                name: input.name,
                description: input.description,
                channel: input.channel,
                groupUuid: input.groupId,
                channelSettings: { channelConfig: input.channelConfig },
            }),
        ),

    /**
     * Revisão atômica do estúdio (M5): dados + channelConfig + deliveryPolicy + grupo + os
     * modelos (conteúdo) e vínculos numa única transação, com versão otimista. Mapeia o tipo do
     * frontend para o payload do backend (channelConfig vai dentro de channelSettings).
     */
    reviseMessageComposition: async (uuid: string, input: ReviseCompositionInput): Promise<SavedRef> => {
        const body = {
            name: input.name,
            description: input.description,
            groupUuid: input.groupId,
            channelSettings: { channelConfig: input.channelConfig },
            deliveryPolicy: input.deliveryPolicy ?? null,
            version: input.version,
            templates: input.templates.map((t, index) => ({
                uuid: isPersistedId(t.id) ? t.id : null,
                role: compositionRole(t),
                position: index,
                name: t.name,
                content: t.content,
                variables: t.variables,
                subject: t.subject,
                providerRef: t.providerRef,
                providerParameters: (t.providerBindings ?? []).map((b) => ({
                    position: b.placeholder,
                    variable: b.variable,
                })),
                editorSource: t.editorSource ?? null,
            })),
        };
        return savedRef(await put(`/message-compositions/${uuid}`, body), uuid);
    },

    duplicateMessageComposition: async (uuid: string): Promise<MessageComposition> =>
        toMessageComposition(unwrap<any>(await post(`/message-compositions/${uuid}/duplicates`, {}))),

    toggleMessageCompositionStatus: async (
        uuid: string,
        reason: string,
    ): Promise<MessageComposition> => {
        const current = await communicationApi.getMessageComposition(uuid);
        const next = current.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        return toMessageComposition(
            unwrap<any>(await patch(`/message-compositions/${uuid}/status`, { status: next, reason })),
        );
    },

    changeMessageCompositionGroup: async (
        uuid: string,
        groupUuid: string | null,
    ): Promise<MessageComposition> =>
        unwrap<MessageComposition>(await patch(`/message-compositions/${uuid}/group`, { groupUuid })),

    /**
     * Prévia stateless do rascunho: o estúdio envia o conteúdo em edição e o servidor renderiza
     * com o renderer real (mesmo do envio) e as constantes do ambiente. Substitui o render Jinja
     * que era feito no cliente.
     */
    previewDraft: async (input: {
        templates: Array<{ role: string; content: string; templateUuid?: string | null }>;
        values: Record<string, string>;
        environment?: string;
    }): Promise<PreviewRender[]> => {
        const data = unwrap<{ renders: PreviewRender[] }>(
            await post(`/message-compositions/preview`, input as unknown as Record<string, unknown>),
        );
        return data.renders ?? [];
    },

    // ============ Group ============
    listGroups: async (filters: { search?: string; status?: string } = {}): Promise<Group[]> => {
        const payload = await get(`/groups/page-list${qs(filters)}`);
        const page = toPageResult<any>(payload);
        return page.list.map(toGroup);
    },

    getGroup: async (uuid: string): Promise<Group> => toGroup(unwrap<any>(await get(`/groups/${uuid}/page-view`))),

    createGroup: async (input: {
        name: string;
        description: string;
        defaultForNewContactPoints?: boolean;
    }): Promise<Group> => toGroup(unwrap<any>(await post(`/groups`, input as unknown as Record<string, unknown>))),

    updateGroup: async (
        uuid: string,
        patchData: { name?: string; description?: string; defaultForNewContactPoints?: boolean },
    ): Promise<Group> =>
        unwrap<Group>(await put(`/groups/${uuid}`, patchData as unknown as Record<string, unknown>)),

    addUserToGroup: async (uuid: string, login: string): Promise<Group> =>
        unwrap<Group>(await post(`/groups/${uuid}/members`, { userLogin: login })),

    removeUserFromGroup: async (uuid: string, login: string, reason: string): Promise<Group> =>
        unwrap<Group>(await del(`/groups/${uuid}/members/${login}`, { reason })),

    toggleGroupStatus: async (uuid: string, reason: string): Promise<Group> => {
        const current = await communicationApi.getGroup(uuid);
        const next = current.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        return unwrap<Group>(await patch(`/groups/${uuid}/status`, { status: next, reason }));
    },

    searchUsers: async (search: string): Promise<DirectoryUser[]> => {
        const payload = await get(`/users${qs({ search })}`);
        return unwrap<DirectoryUser[]>(payload) || [];
    },

    // ============ Routing (read-only) ============
    listRoutings: async (filters: { search?: string } = {}): Promise<Routing[]> => {
        const payload = await get(`/routings/page-list${qs(filters)}`);
        const page = toPageResult<any>(payload);
        return page.list.map(toRouting);
    },

    getRouting: async (ruleId: string): Promise<Routing> =>
        toRouting(unwrap<any>(await get(`/routings/${ruleId}/page-view`))),

    // ============ System constants ============
    listSystemConstants: async (): Promise<SystemConstant[]> => {
        const payload = await get(`/system-constants`);
        return unwrap<SystemConstant[]>(payload) || [];
    },
};

export default communicationApi;
